// Service for interacting with Bazaraki API and scraping property data

import { createNormalizedPropertyData } from './propertyDataSchema';
import { formatDateForInput } from '../utils/dateUtils';
import { interpretAttr } from '../utils/propertyUtils';
import { fetchViaProxies } from './proxyFetch';

/**
 * Fetch raw data from Bazaraki API via CORS proxies.
 * Now uses the shared proxy utility (Worker → cors.sh → free proxies).
 * @param {string} propertyUrl - Bazaraki property URL
 * @returns {Promise<Object>} - Raw Bazaraki API response
 */
const fetchBazarakiRawData = async (propertyUrl) => {
  if (!propertyUrl || !propertyUrl.trim()) {
    throw new Error("Property URL is required");
  }

  const match = propertyUrl.match(/\/adv\/(\d+)(?:_|\/|\?|$)/);
  if (!match || !match[1]) {
    throw new Error("Invalid URL: Cannot extract property ID");
  }

  const itemId = match[1];
  const apiUrl = `https://www.bazaraki.com/api/items/${itemId}`;

  try {
    return await fetchViaProxies(apiUrl, {
      timeout: 15000,
      responseType: 'json',
      label: 'Bazaraki',
      validate: (data) => data && typeof data === 'object',
    });
  } catch {
    throw new Error(
      'All proxy services are currently unavailable. ' +
      'Please try again later or input property data manually.'
    );
  }
};

/**
 * Pick the best cover image from the Bazaraki `images` array.
 * Each entry is `{ url, orig, is_flatplan }` where `url` is a ~160px thumbnail
 * and `orig` is the full-size original. Floor plans are skipped when a photo exists.
 * @param {Array} images
 * @returns {string}
 */
export const pickBazarakiImage = (images) => {
  if (!Array.isArray(images) || images.length === 0) return '';
  const photo = images.find((img) => img && !img.is_flatplan) || images[0];
  return photo?.orig || photo?.url || '';
};

/**
 * Normalize raw Bazaraki API data into the standard property data shape
 * @param {Object} rawData - Raw Bazaraki API response
 * @param {string} url - The original property URL
 * @returns {Object} - Normalized property data
 */
export const normalizeBazarakiData = (rawData, url) => {
  const { forSale, forRent } = detectPropertyType(url, rawData);

  return createNormalizedPropertyData({
    title: rawData.title || "",
    description: rawData.description || "",
    price: parseFloat(rawData.price || "0"),
    startPrice: rawData.start_price ? parseFloat(rawData.start_price).toString() : "",
    publishedDate: formatDateForInput(rawData.created_dt),
    updatedDate: formatDateForInput(rawData.raise_dt),
    area: rawData.attrs?.attrs__area?.toString() || "",
    bedrooms: rawData.attrs?.['attrs__number-of-bedrooms']?.toString() || "",
    bathrooms: rawData.attrs?.['attrs__number-of-bathrooms']?.toString() || "",
    imageURL: pickBazarakiImage(rawData.images),
    latitude: rawData.coordinates?.latitude ?? null,
    longitude: rawData.coordinates?.longitude ?? null,
    cityId: rawData.city?.toString() || "",
    pets: interpretAttr("pets", rawData.attrs?.attrs__pets),
    furnishing: interpretAttr("furnishing", rawData.attrs?.attrs__furnishing),
    parking: interpretAttr("parking", rawData.attrs?.attrs__parking),
    constructionYear: interpretAttr("construction-year", rawData.attrs?.attrs__construction_year),
    onlineViewing: interpretAttr("online-viewing", rawData.attrs?.attrs__online_viewing),
    landlordName: rawData.user?.name || "",
    landlordJoined: rawData.user?.joined ? formatDateForInput(rawData.user.joined) : "",
    ownerAdCount: rawData.owner_advert_count?.toString() || "",
    forSale,
    forRent,
  });
};

/**
 * Fetch and normalize Bazaraki property data
 * @param {string} propertyUrl - Bazaraki property URL
 * @returns {Promise<Object>} - Normalized property data
 */
export const fetchBazarakiData = async (propertyUrl) => {
  const rawData = await fetchBazarakiRawData(propertyUrl);
  return normalizeBazarakiData(rawData, propertyUrl);
};

export const fetchPropertyData = async (url) => {
  // Alias for backward compatibility
  return fetchBazarakiData(url);
};

export const scanSearchResults = async (apiUrl) => {
  // This would implement scanning a search URL for new properties
  
  try {
    // In a real implementation, this would:
    // 1. Call the Bazaraki search API
    // 2. Parse the results
    // 3. Return new properties found
    
    throw new Error('Search scanning not implemented yet');
    
    // Example response:
    /*
    return {
      newProperties: [
        {
          url: 'https://bazaraki.com/property/123',
          title: 'New Property Found',
          price: 180000,
          // ... other details
        }
      ],
      totalFound: 1
    };
    */
  } catch (error) {
    console.error('Error scanning search results:', error);
    throw new Error(`Failed to scan search results: ${error.message}`);
  }
};

export const validateBazarakiUrl = (url) => {
  // Validate if URL is a valid Bazaraki property URL
  const bazarakiPattern = /^https?:\/\/(www\.)?bazaraki\.com\//;
  return bazarakiPattern.test(url);
};

export const detectPropertyType = (url, apiData = null) => {
  // Detect if property is for sale or rent based on URL patterns and API data
  let forSale = false;
  let forRent = false;



  // Check URL patterns first
  const urlLower = url.toLowerCase();
  
  // Common patterns for sale properties
  if (urlLower.includes('/real-estate-for-sale/') || 
      urlLower.includes('/property-for-sale/') ||
      urlLower.includes('/houses-for-sale/') ||
      urlLower.includes('/apartments-for-sale/') ||
      urlLower.includes('/land-for-sale/') ||
      urlLower.includes('/commercial-for-sale/')) {
    forSale = true;

  }
  
  // Common patterns for rent properties
  if (urlLower.includes('/real-estate-for-rent/') || 
      urlLower.includes('/property-for-rent/') ||
      urlLower.includes('/houses-for-rent/') ||
      urlLower.includes('/apartments-for-rent/') ||
      urlLower.includes('/commercial-for-rent/')) {
    forRent = true;

  }

  // Check API data if available
  if (apiData) {
    // Check category or type fields in API response
    const category = apiData.category?.toLowerCase() || '';
    const type = apiData.type?.toLowerCase() || '';
    const title = apiData.title?.toLowerCase() || '';
    
    // Check title for sale/rent indicators - especially the last word
    const titleWords = title.trim().split(/\s+/);
    const lastWord = titleWords[titleWords.length - 1];
    const secondLastWord = titleWords.length > 1 ? titleWords[titleWords.length - 2] : '';
    
    // Check for sale indicators
    if (category.includes('sale') || type.includes('sale') || 
        title.includes('for sale') || title.includes('πώληση') || title.includes('sale') ||
        lastWord === 'sale' || lastWord === 'πώληση' || 
        (secondLastWord === 'for' && lastWord === 'sale')) {
      forSale = true;

    }
    
    // Check for rent indicators  
    if (category.includes('rent') || type.includes('rent') || 
        title.includes('for rent') || title.includes('ενοικίαση') || title.includes('rent') ||
        title.includes('rental') || title.includes('ενοίκιο') ||
        lastWord === 'rent' || lastWord === 'rental' || lastWord === 'ενοικίαση' || lastWord === 'ενοίκιο' ||
        (secondLastWord === 'for' && lastWord === 'rent')) {
      forRent = true;

    }

    // Additional Greek language patterns
    if (title.includes('προς πώληση') || title.includes('πωλείται')) {
      forSale = true;

    }
    
    if (title.includes('προς ενοικίαση') || title.includes('ενοικιάζεται')) {
      forRent = true;

    }
  }

  // Default to sale if nothing detected (most common case)
  if (!forSale && !forRent) {
    forSale = true;

  }


  return { forSale, forRent };
};
